import { useAuth } from "@/auth"
import { useSocket } from "@/providers/SocketProvider"
import React, { useEffect } from "react"
import { cn } from "../../styles/utils"
import { Box } from "../Box"
import { Icon, type IconName } from "../Icons"
import { Pressable } from "../Pressable"
import { Text } from "../Text"
import { useToast } from "../Toast"
import { Tooltip } from "../Tooltip"
import { ConnectionStatus } from "../connection"

type DesktopLayoutProps = {
  children: React.ReactNode
  className?: string
}

export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  children,
  className,
}) => {
  const { addErrorHandler, removeErrorHandler } = useSocket()
  const { showToast } = useToast()

  useEffect(() => {
    const listener = (error: any) => {
      showToast({
        action: "error",
        title: "Server error",
        description: error.message,
        variant: "solid",
        placement: "bottom right",
      })
    }
    addErrorHandler(listener)
    return () => {
      removeErrorHandler(listener)
    }
  }, [addErrorHandler, removeErrorHandler, showToast])

  return (
    <Box className={cn("flex-1 flex-row overflow-hidden", className)}>
      {/* Side Navigation */}
      <SideNav className="h-full" />

      {/* Main Content Area */}
      <Box className="flex-1 flex-col h-full overflow-hidden">{children}</Box>
    </Box>
  )
}

const SideNav: React.FC<{ className?: string }> = ({ className }) => {
  const { logout, isAuthenticated } = useAuth()

  return (
    <Box
      className={cn(
        "w-14 bg-navbar-950 border-r border-border shadow-sm",
        className,
      )}
    >
      <Box className="flex-1">
        <Box className="h-full flex-col items-center justify-between py-4 space-y-4">
          <Box>
            <Box className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center mb-4">
              <Text className="text-typography-0 font-bold text-sm">TF</Text>
            </Box>
            <ConnectionStatus />
          </Box>
          <Box>
            {isAuthenticated && (
              <NavItem icon="logout" tooltip="Logout" onPress={logout} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const NavItem: React.FC<{
  icon: IconName
  active?: boolean
  onPress?: () => void
  tooltip?: string
}> = ({ icon, active = false, onPress, tooltip }) => {
  return (
    <Tooltip content={tooltip ?? ""} placement="bottom">
      <Pressable
        onPress={onPress}
        className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center hover:bg-primary-300",
          active ? "border-primary-900 border-2 bg-primary-300" : "",
        )}
      >
        <Icon
          name={icon}
          className={cn(
            "h-6 w-6 text-primary-400 hover:text-primary-600",
            active ? "text-primary-600" : "",
          )}
        />
      </Pressable>
    </Tooltip>
  )
}
